/**
 * NW Bundle Options - Syncs selected bundle/variant to product form
 *
 * Flow:
 * 1. Main bundle radios (name="nw-bundle-option") select which offer
 * 2. Some offers have variant radios - when selected, that variant ID wins
 * 3. The active variant ID is synced to form input[name="id"] for add-to-cart
 * 4. Featured product: toggles .active on .bundle-offer-gallery matching data-product-id
 */

(function () {
  'use strict';

  const SELECTORS = {
    infoWrapper: '.product__info-wrapper',
    productForm: 'product-form',
    form: 'form[data-type="add-to-cart-form"]',
    variantInput: 'input[name="id"]',
    bundleRadio: '.nw-bundle-option__radio',
    variantRadio: '.nw-bundle-variant__radio',
    bundleOfferGallery: '.bundle-offer-gallery',
    bundleOptionPrice: '[data-bundle-option-price]',
  };

  /**
   * Gets the product form's variant ID input within the given scope
   * @param {Element} scope - Parent element (e.g. product__info-wrapper)
   * @returns {HTMLInputElement|null}
   */
  function getVariantInput(scope) {
    if (!scope) return null;
    const formEl = scope.querySelector(`${SELECTORS.productForm} ${SELECTORS.form}`);
    return formEl?.querySelector(SELECTORS.variantInput) || null;
  }

  /**
   * Ensures the main offer radio is checked when a variant within that offer is selected.
   * Variant cards are inside the offer label, so clicking a variant doesn't check the main radio.
   * @param {Element} variantRadioEl - The variant radio that was selected
   */
  function syncMainOfferFromVariant(variantRadioEl) {
    const wrapper = variantRadioEl?.closest('.nw-bundle-option-wrapper');
    const mainRadio = wrapper?.querySelector(SELECTORS.bundleRadio);
    if (!mainRadio || !variantRadioEl?.value) return;

    mainRadio.checked = true;
    mainRadio.value = variantRadioEl.value;
  }

  /**
   * Gets the effective variant ID from the selected bundle option.
   * If the selected bundle has variant cards and a variant is selected, use that.
   * Otherwise use the main bundle radio's value.
   * @returns {string|null} Variant ID or null
   */
  function getActiveVariantId() {
    const checkedMain = document.querySelector(`${SELECTORS.bundleRadio}:checked`);
    if (!checkedMain) return null;

    const wrapper = checkedMain.closest('.nw-bundle-option-wrapper');
    if (!wrapper) return checkedMain.value || null;

    const variantCards = wrapper.querySelector('.nw-bundle-variant-cards');
    if (variantCards) {
      const checkedVariant = variantCards.querySelector(`${SELECTORS.variantRadio}:checked`);
      if (checkedVariant?.value) return checkedVariant.value;
    }

    return checkedMain.value || null;
  }

  /**
   * Syncs the active bundle/variant selection to the product form
   */
  function syncVariantToForm() {
    const infoWrapper = document.querySelector(SELECTORS.infoWrapper);
    const variantInput = getVariantInput(infoWrapper);
    if (!variantInput) return;

    const variantId = getActiveVariantId();
    if (!variantId) return;

    variantInput.value = variantId;
    variantInput.disabled = false;
  }

  /**
   * Toggles which bundle-offer gallery is visible (matches data-product-id on bundle radio)
   */

  /**
   * Updates the offer card headline price from the selected variant (when variant cards exist).
   */
  function syncBundleDisplayPrices() {
    document.querySelectorAll('.nw-bundle-option-wrapper').forEach(function (wrapper) {
      const variantCards = wrapper.querySelector('.nw-bundle-variant-cards');
      const priceEl = wrapper.querySelector(SELECTORS.bundleOptionPrice);
      if (!priceEl || !variantCards) return;

      const checkedVariant = variantCards.querySelector(`${SELECTORS.variantRadio}:checked`);
      if (!checkedVariant) return;

      const formatted = checkedVariant.getAttribute('data-price');
      if (formatted != null && formatted !== '') {
        priceEl.textContent = formatted;
      }
    });
  }

  function syncBundleGallery() {
    const infoWrapper = document.querySelector(SELECTORS.infoWrapper);
    if (!infoWrapper) return;

    const featuredProduct = infoWrapper.closest('.featured-product');
    if (!featuredProduct) return;

    const galleries = featuredProduct.querySelectorAll(SELECTORS.bundleOfferGallery);
    if (!galleries.length) return;

    const checkedMain = document.querySelector(`${SELECTORS.bundleRadio}:checked`);
    if (!checkedMain) return;

    const productId = checkedMain.getAttribute('data-product-id');
    if (!productId) return;

    galleries.forEach(function (gallery) {
      const gid = gallery.getAttribute('data-bundle-product-id');
      if (gid === productId) {
        gallery.classList.add('active');
      } else {
        gallery.classList.remove('active');
      }
    });
  }

  /**
   * Updates buy-button CTA from the selected bundle product's custom.cta_text metafield.
   */
  function syncCtaText() {
    const infoWrapper = document.querySelector(SELECTORS.infoWrapper);
    if (!infoWrapper) return;

    const productForm = infoWrapper.querySelector(SELECTORS.productForm);
    if (!productForm) return;

    const submitButtonText = productForm.querySelector('[type="submit"] span');
    if (!submitButtonText) return;

    const checkedMain = document.querySelector(`${SELECTORS.bundleRadio}:checked`);
    if (!checkedMain) return;

    const ctaText = checkedMain.getAttribute('data-cta-text');

    if (ctaText) {
      productForm.setAttribute('data-cta-text', ctaText);
      submitButtonText.textContent = ctaText;
    } else {
      productForm.removeAttribute('data-cta-text');
      submitButtonText.textContent = window.variantStrings?.addToCart || submitButtonText.textContent;
    }
  }

  function syncAll() {
    syncVariantToForm();
    syncBundleDisplayPrices();
    syncBundleGallery();
    syncCtaText();
  }

  /**
   * Binds change and click listeners to bundle and variant radios
   */
  function bindListeners() {
    const infoWrapper = document.querySelector(SELECTORS.infoWrapper);
    if (!infoWrapper) return;

    const variantInput = getVariantInput(infoWrapper);
    if (!variantInput) return;

    const hasBundleOptions = document.querySelector(SELECTORS.bundleRadio);
    if (!hasBundleOptions) return;

    // Change event: when variant is selected, also check the parent main offer radio
    document.addEventListener('change', function handleBundleChange(e) {
      const target = e.target;
      if (!target.matches(SELECTORS.bundleRadio) && !target.matches(SELECTORS.variantRadio)) return;

      if (target.matches(SELECTORS.variantRadio)) {
        syncMainOfferFromVariant(target);
      }
      syncAll();
    });

    // Click fallback: when user clicks an offer card or variant card
    document.addEventListener('click', function handleOfferClick(e) {
      const wrapper = e.target.closest('.nw-bundle-option-wrapper');
      if (!wrapper) return;

      const variantCard = e.target.closest('.nw-bundle-variant');

      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (variantCard) {
            const variantRadio = variantCard.querySelector(SELECTORS.variantRadio);
            if (variantRadio?.checked) {
              syncMainOfferFromVariant(variantRadio);
            }
          }
          syncAll();
        });
      });
    });
  }

  /**
   * Initializes the bundle options sync
   */
  function init() {
    bindListeners();
    syncAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector(SELECTORS.bundleRadio)) {
      syncAll();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
